import { CSSProperties, ReactNode, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link as RouterLink, useLocation } from 'react-router-dom';

// project import
import { Collapsible, CollapsibleContent } from 'components/ui/collapsible';
import { Popover, PopoverContent, PopoverTrigger } from 'components/ui/popover';
import useAuth from 'hooks/useAuth';
import { isMenuItemVisible } from 'menu-items/menuVisibility';
import NavItem from './NavItem';
import { matchesNavPathname } from './navItemMatch';

// assets
import { BorderOutlined, DownOutlined, OpenInNew, UpOutlined } from 'components/icons';

// types
import { activeItem } from 'store/reducers/menu.ts';
import { NavItemType } from 'types/menu';
import { RootStateProps } from 'types/root';
import { cn } from 'utils/cn';
import { newTabIconClass, topLevelIconClass, topLevelRowClass } from './navStyles';

// ==============================|| NAVIGATION - LIST COLLAPSE ||============================== //

interface Props {
    menu: NavItemType;
    level: number;
}

// Whether the current pathname lives inside one of `menu.children` — used both for the on-mount
// initial `open` value and the render-time auto-open on pathname/menu changes below.
function shouldAutoOpen(currentPathname: string, currentMenu: NavItemType): boolean {
    const children = currentMenu.children || [];
    const childrenUrls = children.flatMap((child) => (child.id ? [child.id] : []));
    const parts = currentPathname.split('/').filter((part) => part);
    return children.some((item) => {
        const foundUrl = parts.find((part) => childrenUrls.includes(part) || childrenUrls.includes(part + 's'));
        const isExactMatch = item.url === currentPathname;
        return !!(foundUrl || isExactMatch);
    });
}

function NavCollapse({ menu, level }: Props) {
    const { pathname } = useLocation();
    const dispatch = useDispatch();
    const { user } = useAuth();

    const isDisabled = !isMenuItemVisible(menu, user);

    const { drawerOpen, openItem } = useSelector((state: RootStateProps) => state.menu);

    const [open, setOpen] = useState(() => shouldAutoOpen(pathname, menu));
    const [flyoutOpen, setFlyoutOpen] = useState(false);

    useEffect(() => {
        if (matchesNavPathname(pathname, menu.url, menu.altActiveUrls)) {
            dispatch(activeItem({ openItem: [menu.id] }));
        }
        // eslint-disable-next-line
    }, [pathname]);

    // Pathname-driven auto-open on later navigations. Render-time guarded setState via prev-value
    // compare (https://react.dev/reference/react/useState#storing-information-from-previous-renders).
    // Preserves monotonicity for `open` (only opens, never closes via this branch — the row's click
    // handles close). The mount-time case is handled by the lazy initializer on `open` above.
    // The rail's flyout does close here: a tap opens it through the hover trigger, and a touch screen
    // has no pointer to leave with, so it would stay over the page the tap navigated to.
    const [prevPathname, setPrevPathname] = useState(pathname);
    const [prevMenu, setPrevMenu] = useState(menu);
    if (pathname !== prevPathname || menu !== prevMenu) {
        if (pathname !== prevPathname) {
            setFlyoutOpen(false);
        }
        setPrevPathname(pathname);
        setPrevMenu(menu);
        if (shouldAutoOpen(pathname, menu)) {
            setOpen(true);
        }
    }

    // The rail shows the children only in the flyout beside the row, the open drawer lists them under it.
    const children = menu.children?.map((item) => {
        switch (item.type) {
            case 'collapse':
                return <NavCollapse key={item.id} menu={item} level={level + 1} />;
            case 'item':
                return <NavItem isPopup={!drawerOpen} key={item.id} item={item} level={level + 1} />;
            default:
                return null;
        }
    });

    const isSelected =
        openItem[0] === menu.id ||
        (Array.isArray(menu.children) && menu.children.some((child) => child.id === openItem[0]));

    const Icon = menu.icon;
    let icon: ReactNode = null;
    if (Icon) icon = <Icon style={{ fontSize: drawerOpen ? '1rem' : '1.25rem' }} />;
    else if (level === 1) icon = <BorderOutlined style={{ fontSize: '1rem' }} />;

    const showLabel = drawerOpen || level !== 1;

    const rowContent = (
        <>
            {icon && <span className={topLevelIconClass(drawerOpen)}>{icon}</span>}
            {showLabel ? (
                // `overflow-hidden`: mid-open the label is narrower than its text; the chevron must not cover it.
                <span
                    className="text-primary my-1 min-w-0 flex-1 overflow-hidden text-sm/5.5 font-semibold"
                    style={menu.style}
                >
                    {menu.title}
                </span>
            ) : (
                // The collapsed rail drops the label, leaving the row with no accessible name; the
                // icon is decorative. Name it from content, since `title` is a node.
                <span className="sr-only">{menu.title}</span>
            )}
            {showLabel && menu.target === '_blank' && <OpenInNew className={newTabIconClass} />}
            {/* The chevron sits at 10px beside the 14px label. The stroke props are SVG presentation
                attributes in the glyph's 896-unit viewBox, which thicken the thin Ant outline so it
                stays legible at this size. */}
            {showLabel &&
                !menu.showChildrenOnlyOnPopper &&
                (open ? (
                    <UpOutlined
                        className="text-primary ml-px text-[10px] opacity-60"
                        strokeWidth={120}
                        stroke="currentColor"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                ) : (
                    <DownOutlined
                        className="ml-px text-[10px] opacity-60"
                        strokeWidth={120}
                        stroke="currentColor"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                ))}
        </>
    );

    // A group whose children live only in the flyout draws its selected state as a pill in the open
    // drawer; a group listing them underneath marks the selected child instead.
    const rowClassName = cn(
        topLevelRowClass(drawerOpen, isSelected && (!drawerOpen || !!menu.showChildrenOnlyOnPopper)),
        isSelected && 'text-primary',
        menu.disabled && 'opacity-20'
    );
    const rowStyle: CSSProperties | undefined = drawerOpen ? { paddingLeft: level * 24 } : undefined;
    const toggle = drawerOpen ? () => setOpen(!open) : undefined;

    const row = menu.url ? (
        <RouterLink to={menu.url} target={menu.target} className={rowClassName} style={rowStyle} onClick={toggle}>
            {rowContent}
        </RouterLink>
    ) : (
        <button
            type="button"
            className={rowClassName}
            style={rowStyle}
            onClick={toggle}
            aria-expanded={drawerOpen ? open : undefined}
        >
            {rowContent}
        </button>
    );

    if (isDisabled) return null;

    if (drawerOpen) {
        return (
            <li>
                {row}
                {!menu.showChildrenOnlyOnPopper && (
                    <Collapsible open={open}>
                        <CollapsibleContent className="h-(--collapsible-panel-height) overflow-hidden transition-[height] duration-250 ease-in-out data-ending-style:h-0 data-starting-style:h-0 motion-reduce:transition-none">
                            <ul>{children}</ul>
                        </CollapsibleContent>
                    </Collapsible>
                )}
            </li>
        );
    }

    return (
        <li>
            <Popover open={flyoutOpen} onOpenChange={setFlyoutOpen}>
                <PopoverTrigger openOnHover delay={0} nativeButton={!menu.url} render={row} />
                <PopoverContent
                    side="right"
                    align="start"
                    sideOffset={10}
                    // Clears every overlay tier: the flyout opens beside the rail even over an open side panel.
                    positionerClassName="z-(--z-blocking-loader)"
                    // `!`: `cn` does not know `shadow-dropdown` is a shadow, so it cannot drop the kit's `shadow-md`.
                    className="shadow-dropdown! w-auto min-w-[206px] gap-0 p-0 py-2 ring-0"
                >
                    <ul>{children}</ul>
                </PopoverContent>
            </Popover>
        </li>
    );
}

export default NavCollapse;
